import { apiService } from './api';
import { API_BASE_URL } from '../config/api';
import { PaginationParams } from '../types';

// ─── Status types ──────────────────────────────────────────────────────────────

export type PurchaseOrderStatus =
  | 'Draft'
  | 'Submitted'
  | 'Confirmed'
  | 'PartiallyReceived'
  | 'Received'
  | 'Closed';

export type ApprovalStatus =
  | 'Draft'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected';

export type CancellationStatus =
  | 'None'
  | 'PendingCancellation'
  | 'Cancelled'
  | 'CancellationRejected';

export type CancellationRequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Withdrawn';

export type ApprovalRecordStatus = 'Pending' | 'Approved' | 'Rejected';

// ─── Approval DTOs ─────────────────────────────────────────────────────────────

export interface PurchaseOrderApprovalDTO {
  id: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: ApprovalRecordStatus;
  approvedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface PurchaseOrderApprovalStatusDTO {
  purchaseOrderId: number;
  poNumber: string;
  totalAmount: number;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  approvalStatus: ApprovalStatus;
  approvals: PurchaseOrderApprovalDTO[];
}

// ─── Cancellation DTOs ────────────────────────────────────────────────────────

export interface CancellationApprovalDTO {
  id: number;
  approvalLevel: number;
  assignedToUserId: number;
  assignedToUserName?: string;
  approvingUserId?: number;
  approvingUserName?: string;
  status: ApprovalRecordStatus;
  actionedAt?: string;
  approvalComments?: string;
  rejectionReason?: string;
}

export interface CancellationRequestDTO {
  id: number;
  purchaseOrderId: number;
  poNumber: string;
  totalAmount: number;
  requestedByUserId: number;
  requestedByUserName?: string;
  cancellationReason: string;
  status: CancellationRequestStatus;
  currentApprovalLevel: number;
  totalApprovalLevels: number;
  cancelledAt?: string;
  rejectionNotes?: string;
  createdAt: string;
  approvals: CancellationApprovalDTO[];
}

// ─── Purchase Order DTOs ───────────────────────────────────────────────────────

export interface PurchaseOrderLineDTO {
  id: number;
  purchaseOrderId: number;
  productId: number;
  productName?: string;
  productSKU?: string;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  remarks?: string;
  lineTotal: number;
}

// ─── Payment DTOs ─────────────────────────────────────────────────────────────

export type POPaymentMethod = 'Cash' | 'BankTransfer' | 'CreditCard' | 'Cheque';

export interface PurchaseOrderPaymentDTO {
  id: number;
  purchaseOrderId: number;
  amount: number;
  paymentDate: string;
  paymentMethod: POPaymentMethod;
  referenceNumber?: string;
  notes?: string;
  recordedByUserId: number;
  recordedByUserName?: string;
  createdAt: string;
}

export interface RecordPurchaseOrderPaymentRequest {
  amount: number;
  paymentDate: string;
  paymentMethod: POPaymentMethod;
  referenceNumber?: string;
  notes?: string;
}

export interface SettleCreditPurchaseOrderRequest {
  settlementDate: string;
  remarks?: string;
}

// ─── Purchase Order ───────────────────────────────────────────────────────────

export interface PurchaseOrderDTO {
  id: number;
  poNumber: string;
  supplierId: number;
  supplierName?: string;
  supplierCode?: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  status: PurchaseOrderStatus;
  totalAmount: number;
  notes?: string;
  // Approval fields
  currentApprovalLevel: number;
  approvalStatus: ApprovalStatus;
  totalApprovalLevels: number;
  approvalHistory?: PurchaseOrderApprovalDTO[];
  // Cancellation fields
  cancellationStatus: CancellationStatus;
  // Payment fields
  amountPaid: number;
  balanceDue: number;
  paymentStatus: 'Unpaid' | 'PartiallyPaid' | 'Paid';
  payments?: PurchaseOrderPaymentDTO[];
  paymentTypeId?: number;
  paymentTypeName?: string;
  isCredit: boolean;
  isOverdue: boolean;
  creditPeriodDays: number;
  createdAt: string;
  lines?: PurchaseOrderLineDTO[];
}

// ─── Request types ─────────────────────────────────────────────────────────────

export interface CreatePurchaseOrderLineRequest {
  productId: number;
  quantity: number;
  unitPrice: number;
  remarks?: string;
}

export interface CreatePurchaseOrderRequest {
  supplierId: number;
  orderDate: string;
  expectedDeliveryDate?: string;
  paymentTypeId?: number;
  notes?: string;
  lines: CreatePurchaseOrderLineRequest[];
}

export interface UpdatePurchaseOrderRequest {
  supplierId?: number;
  orderDate?: string;
  expectedDeliveryDate?: string;
  status?: PurchaseOrderStatus;
  notes?: string;
  lines: CreatePurchaseOrderLineRequest[];
}

export interface ApprovePurchaseOrderRequest {
  approvalComments?: string;
}

export interface RejectPurchaseOrderRequest {
  rejectionReason: string;
}

// ─── Cancellation request types ───────────────────────────────────────────────

export interface RequestCancellationRequest {
  cancellationReason: string;
}

export interface ApproveCancellationRequest {
  approvalComments?: string;
}

export interface RejectCancellationRequest {
  rejectionReason: string;
}

// ─── Approval Rule types ───────────────────────────────────────────────────────

export type ApprovalRuleType = 'Amount' | 'Role' | 'Combined';
export type ApprovalRuleCategory = 'Purchase' | 'Cancellation' | 'GRN';

export interface ApprovalRuleDTO {
  id: number;
  level: number;
  ruleCategory: ApprovalRuleCategory;
  ruleType: ApprovalRuleType;
  minAmount: number;
  maxAmount?: number;
  requiredRoleId?: number;
  requiredRoleName?: string;
  description: string;
  isActive: boolean;
}

export interface CreateApprovalRuleRequest {
  level: number;
  ruleCategory: ApprovalRuleCategory;
  ruleType: ApprovalRuleType;
  minAmount: number;
  maxAmount?: number;
  requiredRoleId?: number;
  description: string;
  isActive: boolean;
}

// ─── API service ───────────────────────────────────────────────────────────────

const PURCHASE_ORDERS_API = '/purchase-orders';
const APPROVAL_SETTINGS_API = '/approval-settings';

export const purchaseOrderService = {
  // CRUD
  getPurchaseOrders: (params: PaginationParams) =>
    apiService.get<PurchaseOrderDTO[]>(PURCHASE_ORDERS_API, {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 50,
    }),

  getPurchaseOrderById: (id: string | number) =>
    apiService.get<PurchaseOrderDTO>(`${PURCHASE_ORDERS_API}/${id}`),

  createPurchaseOrder: (data: CreatePurchaseOrderRequest) =>
    apiService.post<PurchaseOrderDTO>(PURCHASE_ORDERS_API, data),

  updatePurchaseOrder: (id: string | number, data: UpdatePurchaseOrderRequest) =>
    apiService.put<PurchaseOrderDTO>(`${PURCHASE_ORDERS_API}/${id}`, data),

  deletePurchaseOrder: (id: string | number) =>
    apiService.delete<void>(`${PURCHASE_ORDERS_API}/${id}`),

  // Workflow
  submitPurchaseOrder: (id: string | number) =>
    apiService.post<PurchaseOrderDTO>(`${PURCHASE_ORDERS_API}/${id}/submit`, {}),

  // Approval actions
  getApprovalStatus: (id: string | number) =>
    apiService.get<PurchaseOrderApprovalStatusDTO>(`${PURCHASE_ORDERS_API}/${id}/approvals`),

  approvePurchaseOrder: (
    id: string | number,
    approvalId: number,
    data: ApprovePurchaseOrderRequest,
  ) =>
    apiService.post<PurchaseOrderApprovalStatusDTO>(
      `${PURCHASE_ORDERS_API}/${id}/approvals/${approvalId}/approve`,
      data,
    ),

  rejectPurchaseOrder: (
    id: string | number,
    approvalId: number,
    data: RejectPurchaseOrderRequest,
  ) =>
    apiService.post<PurchaseOrderApprovalStatusDTO>(
      `${PURCHASE_ORDERS_API}/${id}/approvals/${approvalId}/reject`,
      data,
    ),

  getPendingApprovals: (params: PaginationParams) =>
    apiService.get<PurchaseOrderApprovalStatusDTO[]>(`${PURCHASE_ORDERS_API}/approvals/pending`, {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 50,
    }),

  // ── Cancellation ──────────────────────────────────────────────────────────

  requestCancellation: (id: string | number, data: RequestCancellationRequest) =>
    apiService.post<CancellationRequestDTO>(`${PURCHASE_ORDERS_API}/${id}/cancellation`, data),

  getCancellationStatus: (id: string | number) =>
    apiService.get<CancellationRequestDTO>(`${PURCHASE_ORDERS_API}/${id}/cancellation`),

  withdrawCancellation: (id: string | number) =>
    apiService.post<CancellationRequestDTO>(
      `${PURCHASE_ORDERS_API}/${id}/cancellation/withdraw`,
      {},
    ),

  approveCancellation: (approvalId: number, data: ApproveCancellationRequest) =>
    apiService.post<CancellationRequestDTO>(
      `${PURCHASE_ORDERS_API}/cancellation/approvals/${approvalId}/approve`,
      data,
    ),

  rejectCancellation: (approvalId: number, data: RejectCancellationRequest) =>
    apiService.post<CancellationRequestDTO>(
      `${PURCHASE_ORDERS_API}/cancellation/approvals/${approvalId}/reject`,
      data,
    ),

  getPendingCancellationApprovals: (params: PaginationParams) =>
    apiService.get<CancellationRequestDTO[]>(
      `${PURCHASE_ORDERS_API}/cancellation/approvals/pending`,
      {
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 50,
      },
    ),

  // ── PDF & Email ───────────────────────────────────────────────────────────

  /**
   * Fetches the PO PDF as a Blob and opens it in a new browser tab.
   *
   * IMPORTANT: The window must be opened SYNCHRONOUSLY (before any await) so the
   * call is still inside the original user-gesture context; otherwise popup-blockers
   * will silently suppress window.open().
   */
  openPdfInNewTab: async (id: string | number): Promise<void> => {
    const token = localStorage.getItem('auth_token') ?? '';
    const url = `${API_BASE_URL}/purchase-orders/${id}/pdf`;

    // 1. Open the tab synchronously — still within the click-gesture context.
    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
      throw new Error('popup_blocked');
    }

    // 2. Show a loading placeholder while fetching.
    newTab.document.write(
      '<!DOCTYPE html><html><head><title>Loading PDF…</title>' +
      '<style>body{font-family:Arial,sans-serif;display:flex;align-items:center;' +
      'justify-content:center;height:100vh;margin:0;color:#555;background:#f8f9fa}' +
      'p{font-size:16px}</style></head>' +
      '<body><p>Loading purchase order PDF…</p></body></html>',
    );

    // 3. Fetch the PDF and navigate the already-open tab.
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        // Try to extract a server-side message for better diagnostics.
        let serverMsg = `${response.status} ${response.statusText}`;
        try {
          const errBody = await response.clone().json();
          if (errBody?.error?.message) serverMsg = errBody.error.message;
        } catch { /* ignore – body may not be JSON */ }
        newTab.close();
        throw new Error(serverMsg);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      newTab.location.href = objectUrl;
      // Revoke after 60 s to free memory once the tab has loaded.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      newTab.close();
      throw err;
    }
  },

  /**
   * Sends the approved PO PDF to the supplier via email (backend SMTP).
   */
  sendToSupplier: (id: string | number) =>
    apiService.post<string>(`${PURCHASE_ORDERS_API}/${id}/send-to-supplier`, {}),

  // ── Payments ──────────────────────────────────────────────────────────────

  getPayments: (id: string | number) =>
    apiService.get<PurchaseOrderPaymentDTO[]>(`${PURCHASE_ORDERS_API}/${id}/payments`),

  recordPayment: (id: string | number, data: RecordPurchaseOrderPaymentRequest) =>
    apiService.post<PurchaseOrderPaymentDTO>(`${PURCHASE_ORDERS_API}/${id}/payments`, data),

  getOutstandingOrders: (params?: PaginationParams & { supplierId?: number }) =>
    apiService.get<PurchaseOrderDTO[]>(`${PURCHASE_ORDERS_API}/outstanding`, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 50,
      ...(params?.supplierId && { supplierId: params.supplierId }),
    }),

  settleCreditOrder: (id: string | number, data: SettleCreditPurchaseOrderRequest) =>
    apiService.post<PurchaseOrderDTO>(`${PURCHASE_ORDERS_API}/${id}/settle-credit`, data),
};

// ─── Approval settings service ────────────────────────────────────────────────

export const approvalSettingsService = {
  getApprovalRules: (category?: ApprovalRuleCategory) =>
    apiService.get<ApprovalRuleDTO[]>(`${APPROVAL_SETTINGS_API}/rules`, category ? { category } : {}),

  getApprovalRuleByLevel: (level: number, category: ApprovalRuleCategory = 'Purchase') =>
    apiService.get<ApprovalRuleDTO>(`${APPROVAL_SETTINGS_API}/rules/${level}`, { category }),

  createApprovalRule: (data: CreateApprovalRuleRequest) =>
    apiService.post<ApprovalRuleDTO>(`${APPROVAL_SETTINGS_API}/rules`, data),

  updateApprovalRule: (id: number, data: CreateApprovalRuleRequest) =>
    apiService.put<ApprovalRuleDTO>(`${APPROVAL_SETTINGS_API}/rules/${id}`, data),

  deleteApprovalRule: (id: number) =>
    apiService.delete<void>(`${APPROVAL_SETTINGS_API}/rules/${id}`),
};
